// src/components/Register.js

import React, { useState } from 'react';
import axios from 'axios';
import { 
    TextField, 
    Button, 
    Box, 
    Typography, 
    Paper,
    FormControlLabel,
    Checkbox,
    Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config';
import PageHead from '../components/PageHead';

const Register = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        termsAndConsent: false
    });
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});

    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: event.target.type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        // Validate terms acceptance
        if (!formData.termsAndConsent) {
            setErrors({ terms: 'You must accept the Terms of Service, Privacy Policy, and marketing communications' });
            return;
        }
        
        try {
            const response = await axios.post(`${getApiUrl()}/auth/register`, {
                email: formData.email,
                password: formData.password,
                first_name: formData.firstName,
                last_name: formData.lastName,
                marketing_consent: formData.termsAndConsent
            });
            
            setMessage(response.data.message);
            if (response.data.success) {
                setTimeout(() => navigate('/login'), 2000);
            }
        } catch (error) {
            console.error('Error during registration:', error);
            setMessage(error.response?.data?.error || 'An error occurred');
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(120deg, #2575fc 0%, #6a11cb 100%)',
                padding: '20px',
            }}
        >
            <PageHead 
                title="Create Account" 
                description="Sign up for AutoBlogPilot and start automating your blog content"
            />
            <Paper 
                elevation={3} 
                sx={{ 
                    padding: 4, 
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
            >
                <Typography 
                    variant="h4" 
                    align="center" 
                    gutterBottom
                    sx={{ 
                        fontWeight: 800,
                        background: 'linear-gradient(120deg, #2575fc 0%, #6a11cb 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 3
                    }}
                >
                    Create Account
                </Typography>

                {message && (
                    <Typography 
                        color={message.includes('success') ? 'primary' : 'error'} 
                        align="center"
                        sx={{ mb: 2 }}
                    >
                        {message}
                    </Typography>
                )}

                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Email"
                        name="email"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Password"
                        name="password"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="First Name"
                        name="firstName"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Last Name"
                        name="lastName"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        sx={{ mb: 3 }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                name="termsAndConsent"
                                checked={formData.termsAndConsent}
                                onChange={handleChange}
                                required
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I agree to the{' '}
                                <Link href="https://autoblogpilot.com/terms-of-service" target="_blank" underline="hover">
                                    Terms of Service
                                </Link>
                                {' '}and{' '}
                                <Link href="https://autoblogpilot.com/privacy-policy" target="_blank" underline="hover">
                                    Privacy Policy
                                </Link>
                                {' '}and consent to receive updates, tips, and marketing communications
                            </Typography>
                        }
                        sx={{ mb: 2 }}
                    />
                    {errors.terms && (
                        <Typography color="error" variant="caption" sx={{ mb: 2, display: 'block' }}>
                            {errors.terms}
                        </Typography>
                    )}

                    <Button 
                        type="submit" 
                        fullWidth
                        sx={{
                            background: 'linear-gradient(120deg, #2575fc 0%, #6a11cb 100%)',
                            color: 'white',
                            padding: '12px',
                            borderRadius: '8px',
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                transition: 'transform 0.2s',
                                background: 'linear-gradient(120deg, #2575fc 0%, #6a11cb 100%)',
                            }
                        }}
                    >
                        Register
                    </Button>
                </form>

                <Typography 
                    align="center" 
                    sx={{ 
                        mt: 3,
                        '& a': {
                            color: '#2575fc',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }
                    }}
                >
                    Already registered? <a href="/login">Log in here</a>
                </Typography>
            </Paper>
        </Box>
    );
};

export default Register;
